import { useEffect, useState, useRef, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Search, Send, RefreshCw, MessageSquare, ArrowLeft, Clock, Check, CheckCheck,
  AlertCircle, Lock, User as UserIcon, CalendarDays,
} from "lucide-react";
import { cn } from "@/lib/utils";

const FILTERS = [
  { key: "all", label: "Todas" },
  { key: "unread", label: "Não lidas" },
  { key: "replied", label: "Com resposta" },
  { key: "failed", label: "Falhas de envio" },
];

const POLL_MS = 15000;

function timeLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" });
  const yest = new Date(now); yest.setDate(now.getDate() - 1);
  if (d.toDateString() === yest.toDateString()) return "Ontem";
  return d.toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit" });
}

function fullTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("pt-PT", {
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
  });
}

function StatusIcon({ status }) {
  if (status === "read")
    return <CheckCheck className="w-3.5 h-3.5 text-sky-500" data-testid="msg-status-read" />;
  if (status === "delivered")
    return <CheckCheck className="w-3.5 h-3.5 text-zinc-400" data-testid="msg-status-delivered" />;
  if (status === "sent")
    return <Check className="w-3.5 h-3.5 text-zinc-400" data-testid="msg-status-sent" />;
  if (status === "queued" || status === "sending")
    return <Clock className="w-3.5 h-3.5 text-zinc-400" data-testid="msg-status-queued" />;
  if (status === "failed" || status === "blocked")
    return <AlertCircle className="w-3.5 h-3.5 text-red-500" data-testid="msg-status-failed" />;
  return null;
}

function Avatar({ name, phone }) {
  const letter = (name || phone || "?").trim().slice(0, 1).toUpperCase();
  return (
    <div className="w-10 h-10 rounded-full bg-emerald-600 text-white text-sm font-semibold flex items-center justify-center shrink-0">
      {letter}
    </div>
  );
}

function ConversationItem({ conv, active, onClick }) {
  const title = conv.promoter_name || conv.phone || conv.phone_key;
  const outboundState =
    conv.last_direction !== "inbound" && ["queued", "sending", "sent", "delivered", "read", "failed", "blocked"].includes(conv.last_status);
  return (
    <button
      onClick={onClick}
      data-testid={`inbox-conversation-${conv.phone_key}`}
      className={cn(
        "w-full text-left px-4 py-3 flex gap-3 border-b border-zinc-100 transition-colors",
        active ? "bg-emerald-50" : "hover:bg-zinc-50",
      )}
    >
      <Avatar name={conv.promoter_name} phone={conv.phone} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold text-sm text-zinc-900 truncate">{title}</span>
          <span className="text-[11px] text-zinc-400 shrink-0">{timeLabel(conv.last_message_at)}</span>
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          {conv.last_direction === "inbound" ? (
            <span className="text-[11px] text-emerald-600 shrink-0">↩</span>
          ) : (
            <span className="shrink-0"><StatusIcon status={conv.last_status} /></span>
          )}
          <span className={cn("text-xs truncate", conv.unread_count > 0 ? "text-zinc-900 font-medium" : "text-zinc-500")}>
            {conv.last_message || "—"}
          </span>
        </div>
        <div className="flex items-center gap-2 mt-1">
          {conv.event_name && (
            <span className="inline-flex items-center gap-1 text-[10px] text-zinc-500 bg-zinc-100 rounded px-1.5 py-0.5 max-w-[60%] truncate">
              <CalendarDays className="w-2.5 h-2.5" /> {conv.event_name}
            </span>
          )}
          {conv.has_failed_outbound && (
            <span className="text-[10px] text-red-600 bg-red-50 rounded px-1.5 py-0.5" data-testid={`inbox-failed-badge-${conv.phone_key}`}>
              {conv.failed_reason ? `Falhou — ${conv.failed_reason}` : "Falha"}
            </span>
          )}
        </div>
      </div>
      {conv.unread_count > 0 && (
        <span
          data-testid={`inbox-unread-${conv.phone_key}`}
          className="self-center inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold"
        >
          {conv.unread_count}
        </span>
      )}
    </button>
  );
}

export default function WhatsappInbox() {
  const [conversations, setConversations] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loadingList, setLoadingList] = useState(true);
  const [active, setActive] = useState(null); // phone_key
  const [thread, setThread] = useState(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [reopening, setReopening] = useState(false);
  const bottomRef = useRef(null);
  const activeRef = useRef(null);
  activeRef.current = active;

  const loadList = useCallback(async () => {
    try {
      const { data } = await api.get("/inbox/conversations", {
        params: { filter, search: search || undefined },
      });
      setConversations(data || []);
    } catch (e) {
      console.debug("Inbox conversations poll failed (will retry):", e?.message || e);
    } finally {
      setLoadingList(false);
    }
  }, [filter, search]);

  const loadThread = useCallback(async (key, markRead = false) => {
    if (!key) return;
    try {
      const { data } = await api.get(`/inbox/conversations/${key}/messages`);
      setThread(data);
      if (markRead) {
        try {
          await api.post(`/inbox/conversations/${key}/read`);
        } catch (e) {
          console.debug("Mark-as-read failed:", e?.message || e);
        }
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  // polling
  useEffect(() => {
    const id = setInterval(() => {
      loadList();
      if (activeRef.current) loadThread(activeRef.current, false);
    }, POLL_MS);
    return () => clearInterval(id);
  }, [loadList, loadThread]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [thread?.messages?.length, active]);

  const openConversation = async (key) => {
    setActive(key);
    setThread(null);
    setLoadingThread(true);
    await loadThread(key, true);
    setLoadingThread(false);
    // update badge counts after read
    setConversations((prev) => prev.map((c) => (c.phone_key === key ? { ...c, unread_count: 0 } : c)));
  };

  const sendReply = async () => {
    const text = replyText.trim();
    if (!text || !active) return;
    setSending(true);
    try {
      await api.post(`/inbox/conversations/${active}/reply`, { text });
      setReplyText("");
      await loadThread(active, false);
      loadList();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const reopenConversation = async () => {
    if (!active) return;
    setReopening(true);
    try {
      const { data } = await api.post(`/inbox/conversations/${active}/reopen`);
      toast.success(`Template de reabertura enviado (${data?.template || "janela_24h"}).`);
      await loadThread(active, false);
      loadList();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setReopening(false);
    }
  };

  const eventAction = async (eventId, status) => {
    if (!eventId) return;
    try {
      await api.post(`/events/${eventId}/inbox-response/${thread?.promoter?.id}`, { status });
      const labels = { disponivel: "Disponível", indisponivel: "Indisponível", removido: "Removido do evento" };
      toast.success(`${thread?.promoter?.name || "Promotor"} marcado como ${labels[status]}.`);
      await loadThread(active, false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const windowOpen = thread?.window_open;
  const threadReady = !!thread;
  const contactTitle = thread?.promoter?.name || thread?.phone || active;

  return (
    <div data-testid="whatsapp-inbox-page">
      <div className="mb-5">
        <h1 className="font-display text-2xl font-bold text-zinc-950 flex items-center gap-2">
          <MessageSquare className="w-6 h-6 text-emerald-600" /> WhatsApp Inbox
        </h1>
        <p className="text-sm text-zinc-500 mt-1">Vê quem respondeu e responde diretamente pelo PromoCrew.</p>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden flex h-[calc(100vh-220px)] min-h-[500px]">
        {/* Conversation list */}
        <div className={cn(
          "w-full lg:w-[360px] border-r border-zinc-200 flex flex-col shrink-0",
          active ? "hidden lg:flex" : "flex",
        )}>
          <div className="p-3 border-b border-zinc-100 space-y-2">
            <div className="relative">
              <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="inbox-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Procurar por nome ou número"
                className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-zinc-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
              />
            </div>
            <div className="flex gap-1 flex-wrap">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  data-testid={`inbox-filter-${f.key}`}
                  onClick={() => setFilter(f.key)}
                  className={cn(
                    "text-xs px-2.5 py-1 rounded-full transition-colors",
                    filter === f.key ? "bg-emerald-600 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200",
                  )}
                >
                  {f.label}
                </button>
              ))}
              <button
                data-testid="inbox-refresh"
                onClick={() => { setLoadingList(true); loadList(); }}
                className="ml-auto text-zinc-400 hover:text-zinc-700 p-1"
                title="Atualizar"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="inbox-conversation-list">
            {loadingList ? (
              <div className="p-8 text-center text-sm text-zinc-400">A carregar…</div>
            ) : conversations.length === 0 ? (
              <div className="p-8 text-center text-sm text-zinc-400">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 text-zinc-300" />
                Sem conversas para mostrar.
              </div>
            ) : (
              conversations.map((c) => (
                <ConversationItem
                  key={c.phone_key}
                  conv={c}
                  active={active === c.phone_key}
                  onClick={() => openConversation(c.phone_key)}
                />
              ))
            )}
          </div>
        </div>

        {/* Thread */}
        <div className={cn("flex-1 flex-col", active ? "flex" : "hidden lg:flex")}>
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center text-zinc-400">
              <MessageSquare className="w-12 h-12 mb-3 text-zinc-200" />
              <p className="text-sm">Seleciona uma conversa para ver o histórico.</p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="px-4 py-3 border-b border-zinc-100 flex items-center gap-3">
                <button className="lg:hidden text-zinc-500" onClick={() => setActive(null)} data-testid="inbox-back">
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <Avatar name={thread?.promoter?.name} phone={thread?.phone} />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm text-zinc-900 truncate flex items-center gap-2">
                    {contactTitle}
                    {thread?.promoter && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 bg-emerald-50 rounded px-1.5 py-0.5">
                        <UserIcon className="w-2.5 h-2.5" /> Promotor
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 truncate flex items-center gap-2">
                    {thread?.phone && <span>{thread.phone}</span>}
                    {thread?.event && (
                      <span className="inline-flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {thread.event.name}</span>
                    )}
                  </div>
                </div>
                <span
                  data-testid="inbox-window-status"
                  className={cn(
                    "text-[11px] px-2 py-1 rounded-full font-medium shrink-0",
                    !threadReady ? "bg-zinc-100 text-zinc-400"
                      : windowOpen ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-500",
                  )}
                >
                  {!threadReady ? "A carregar…" : windowOpen ? "Janela 24h aberta" : "Janela 24h fechada"}
                </span>
              </div>

              {/* Event quick-actions: record a free-text reply like a link response */}
              {threadReady && thread?.promoter && (thread?.invited_events || []).length > 0 && (
                <div className="px-4 py-3 border-b border-zinc-100 bg-zinc-50 space-y-2" data-testid="inbox-event-actions">
                  <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">
                    Ações do evento
                  </div>
                  {(thread.invited_events || []).map((ev) => {
                    const statusMap = {
                      confirmado: { t: "Confirmado", c: "bg-emerald-100 text-emerald-700" },
                      disponivel: { t: "Disponível", c: "bg-emerald-100 text-emerald-700" },
                      indisponivel: { t: "Indisponível", c: "bg-red-100 text-red-700" },
                      removido: { t: "Removido", c: "bg-zinc-200 text-zinc-600" },
                      suplente: { t: "Suplente", c: "bg-amber-100 text-amber-700" },
                      sem_resposta: { t: "Sem resposta", c: "bg-zinc-100 text-zinc-500" },
                    };
                    const st = statusMap[ev.current_status] || statusMap.sem_resposta;
                    return (
                      <div key={ev.event_id} className="flex items-center justify-between gap-2 flex-wrap" data-testid={`inbox-event-row-${ev.event_id}`}>
                        <div className="min-w-0 flex items-center gap-2">
                          <CalendarDays className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
                          <span className="text-sm text-zinc-800 truncate">{ev.name}</span>
                          {ev.date && <span className="text-[11px] text-zinc-400">{new Date(ev.date).toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit" })}</span>}
                          <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full font-medium", st.c)}>{st.t}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            data-testid={`inbox-event-available-${ev.event_id}`}
                            onClick={() => eventAction(ev.event_id, "disponivel")}
                            className="text-[11px] px-2 py-1 rounded-md bg-white border border-emerald-200 text-emerald-700 hover:bg-emerald-50 transition-colors"
                          >Disponível</button>
                          <button
                            data-testid={`inbox-event-unavailable-${ev.event_id}`}
                            onClick={() => eventAction(ev.event_id, "indisponivel")}
                            className="text-[11px] px-2 py-1 rounded-md bg-white border border-red-200 text-red-700 hover:bg-red-50 transition-colors"
                          >Indisponível</button>
                          <button
                            data-testid={`inbox-event-remove-${ev.event_id}`}
                            onClick={() => eventAction(ev.event_id, "removido")}
                            className="text-[11px] px-2 py-1 rounded-md bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-100 transition-colors"
                          >Remover</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-zinc-50" data-testid="inbox-thread">
                {loadingThread ? (
                  <div className="text-center text-sm text-zinc-400 py-8">A carregar conversa…</div>
                ) : (thread?.messages || []).length === 0 ? (
                  <div className="text-center text-sm text-zinc-400 py-8">Sem mensagens.</div>
                ) : (
                  (thread?.messages || []).map((m) => {
                    const inbound = m.direction === "inbound";
                    return (
                      <div key={m.id} className={cn("flex", inbound ? "justify-start" : "justify-end")}>
                        <div
                          data-testid={`inbox-message-${m.id}`}
                          className={cn(
                            "max-w-[78%] rounded-2xl px-3 py-2 text-sm shadow-sm",
                            inbound ? "bg-white text-zinc-900 rounded-bl-sm" : "bg-emerald-600 text-white rounded-br-sm",
                          )}
                        >
                          {!inbound && m.kind && m.kind !== "inbox_reply" && (
                            <div className="text-[10px] opacity-80 mb-0.5">{m.kind_label}</div>
                          )}
                          <div className="whitespace-pre-wrap break-words">{m.body || <span className="opacity-60 italic">(sem texto)</span>}</div>
                          <div className={cn("flex items-center gap-1 mt-1 justify-end", inbound ? "text-zinc-400" : "text-emerald-100")}>
                            <span className="text-[10px]">{timeLabel(m.created_at)}</span>
                            {!inbound && <StatusIcon status={m.status} />}
                          </div>
                          {m.error && (
                            <div className="mt-1 text-[11px] flex items-start gap-1 text-red-100 bg-red-500/30 rounded px-1.5 py-1" data-testid={`inbox-error-${m.id}`}>
                              <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                              <span>{m.error}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={bottomRef} />
              </div>

              {/* Reply */}
              {!threadReady ? null : windowOpen ? (
                <div className="p-3 border-t border-zinc-100 flex items-end gap-2">
                  <textarea
                    data-testid="inbox-reply-input"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); }
                    }}
                    rows={1}
                    placeholder="Escreve uma mensagem…"
                    className="flex-1 resize-none max-h-32 px-3 py-2 text-sm rounded-lg border border-zinc-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                  />
                  <button
                    data-testid="inbox-reply-send"
                    onClick={sendReply}
                    disabled={sending || !replyText.trim()}
                    className="h-10 px-4 rounded-lg bg-emerald-600 text-white text-sm font-medium flex items-center gap-1.5 disabled:opacity-40 hover:bg-emerald-700 transition-colors"
                  >
                    <Send className="w-4 h-4" /> Enviar
                  </button>
                </div>
              ) : (
                <div className="p-3 border-t border-zinc-100 bg-amber-50" data-testid="inbox-window-closed-banner">
                  <div className="flex items-start gap-2 text-sm text-amber-800">
                    <Lock className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>
                      A janela de 24h está fechada. Só podes responder em texto livre até 24h após a última
                      mensagem do contacto. As mensagens automáticas (convites, confirmações, lembretes) continuam a ser enviadas com templates aprovados.
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
